import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, TrendingUp, TrendingDown, Wallet, Filter, Download, Calendar, BookOpen, PieChart, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountsPage = () => {
  const { getAuthHeader } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ income: 0, expense: 0, balance: 0 });
  const [breakdown, setBreakdown] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('ledger');
  const [sourceFilter, setSourceFilter] = useState('all');
  
  // Filters
  const [dateFilter, setDateFilter] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [typeFilter, setTypeFilter] = useState('all');

  const [formData, setFormData] = useState({
    type: 'income',
    category_id: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    reference_number: ''
  });
  
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    type: 'income',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, [dateFilter, typeFilter, sourceFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch unified ledger with all sources
      const params = new URLSearchParams({
        start_date: dateFilter.start,
        end_date: dateFilter.end
      });
      if (typeFilter !== 'all') params.append('transaction_type', typeFilter);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      
      const [ledgerRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/accounts/unified-ledger?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/accounts/categories`, { headers: getAuthHeader() })
      ]);
      
      setTransactions(ledgerRes.data.entries || []);
      setCategories(categoriesRes.data);
      setStats({
        income: ledgerRes.data.summary?.total_income || 0,
        expense: ledgerRes.data.summary?.total_expense || 0,
        balance: ledgerRes.data.summary?.net_balance || 0
      });
      setBreakdown(ledgerRes.data.breakdown || {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load accounts data');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredTransactions = () => {
    return transactions;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/accounts/transactions`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, { headers: getAuthHeader() });
      
      toast.success('Transaction added successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/accounts/categories`, categoryFormData, {
        headers: getAuthHeader()
      });
      
      toast.success('Category added successfully');
      setCategoryDialogOpen(false);
      setCategoryFormData({ name: '', type: 'income', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'income',
      category_id: '',
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      payment_method: 'cash',
      reference_number: ''
    });
  };

  const filteredTransactions = getFilteredTransactions();
  const filteredStats = {
    income: filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0),
    expense: filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0)
  };

  // Category-wise summary
  const getCategorySummary = (type) => {
    const typeCategories = categories.filter(c => c.type === type);
    return typeCategories.map(cat => {
      const total = filteredTransactions
        .filter(t => t.category_id === cat.id)
        .reduce((sum, t) => sum + t.amount, 0);
      return { ...cat, total };
    }).sort((a, b) => b.total - a.total);
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Source', 'Type', 'Category', 'Description', 'Reference', 'Amount'];
    const rows = filteredTransactions.map(t => {
      return [
        t.date,
        t.source || 'manual',
        t.type,
        t.category || '-',
        `"${(t.description || '').replace(/"/g, '""')}"`,
        t.reference || '-',
        t.amount
      ].join(',');
    });
    
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ledger_${dateFilter.start}_to_${dateFilter.end}.csv`;
    a.click();
    toast.success('Ledger exported successfully');
  };

  return (
    <DashboardLayout title="Accounts & Finance">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card data-testid="income-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Income</CardTitle>
                <div className="bg-green-500 p-2 rounded-lg">
                  <TrendingUp className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">₹{stats.income.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Fee: ₹{(breakdown.fee_collection || 0).toLocaleString('en-IN')} | Store: ₹{(breakdown.store || 0).toLocaleString('en-IN')}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card data-testid="expense-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Expense</CardTitle>
                <div className="bg-red-500 p-2 rounded-lg">
                  <TrendingDown className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">₹{stats.expense.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Salary: ₹{(breakdown.salary || 0).toLocaleString('en-IN')} | Other: ₹{(breakdown.manual_expense || 0).toLocaleString('en-IN')}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card data-testid="balance-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Net Balance</CardTitle>
                <div className="bg-blue-500 p-2 rounded-lg">
                  <Wallet className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${stats.balance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  ₹{stats.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Income - Expense</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card data-testid="transactions-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Transactions</CardTitle>
                <div className="bg-purple-500 p-2 rounded-lg">
                  <BookOpen className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{transactions.length}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {dateFilter.start} to {dateFilter.end}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Source Filter */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-2">
              <Badge 
                variant={sourceFilter === 'all' ? 'default' : 'outline'} 
                className="cursor-pointer"
                onClick={() => setSourceFilter('all')}
              >
                All Sources
              </Badge>
              <Badge 
                variant={sourceFilter === 'fee_collection' ? 'default' : 'outline'} 
                className="cursor-pointer bg-green-500"
                onClick={() => setSourceFilter('fee_collection')}
              >
                Fee Collection (₹{(breakdown.fee_collection || 0).toLocaleString('en-IN')})
              </Badge>
              <Badge 
                variant={sourceFilter === 'salary' ? 'default' : 'outline'} 
                className="cursor-pointer bg-red-500"
                onClick={() => setSourceFilter('salary')}
              >
                Salary (₹{(breakdown.salary || 0).toLocaleString('en-IN')})
              </Badge>
              <Badge 
                variant={sourceFilter === 'store' ? 'default' : 'outline'} 
                className="cursor-pointer bg-blue-500"
                onClick={() => setSourceFilter('store')}
              >
                Store (₹{(breakdown.store || 0).toLocaleString('en-IN')})
              </Badge>
              <Badge 
                variant={sourceFilter === 'manual' ? 'default' : 'outline'} 
                className="cursor-pointer bg-purple-500"
                onClick={() => setSourceFilter('manual')}
              >
                Manual Entries
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-transaction-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Transaction
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add New Transaction</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="type">Type *</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) => setFormData({ ...formData, type: value, category_id: '' })}
                    >
                      <SelectTrigger id="type" data-testid="type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="income">Income</SelectItem>
                        <SelectItem value="expense">Expense</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    <Select
                      value={formData.category_id}
                      onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                    >
                      <SelectTrigger id="category" data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories
                          .filter(c => c.type === formData.type)
                          .map(category => (
                            <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount (₹) *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      required
                      data-testid="amount-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      data-testid="date-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="payment-method">Payment Method *</Label>
                    <Select
                      value={formData.payment_method}
                      onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                    >
                      <SelectTrigger id="payment-method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reference">Reference No.</Label>
                    <Input
                      id="reference"
                      value={formData.reference_number}
                      onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter transaction details"
                    required
                    data-testid="description-input"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} data-testid="submit-transaction-btn">
                    {loading ? 'Adding...' : 'Add Transaction'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-category-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Category</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCategorySubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="cat-type">Type *</Label>
                  <Select
                    value={categoryFormData.type}
                    onValueChange={(value) => setCategoryFormData({ ...categoryFormData, type: value })}
                  >
                    <SelectTrigger id="cat-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="income">Income</SelectItem>
                      <SelectItem value="expense">Expense</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cat-name">Category Name *</Label>
                  <Input
                    id="cat-name"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                    placeholder="e.g., Tuition Fees, Salary, Electricity Bill"
                    required
                    data-testid="category-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cat-desc">Description</Label>
                  <Textarea
                    id="cat-desc"
                    value={categoryFormData.description}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                    placeholder="Optional description"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} data-testid="submit-category-btn">
                    {loading ? 'Adding...' : 'Add Category'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Button variant="outline" onClick={exportToCSV} data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="ledger" className="gap-2">
              <BookOpen className="h-4 w-4" />
              Ledger
            </TabsTrigger>
            <TabsTrigger value="categories" className="gap-2">
              <Filter className="h-4 w-4" />
              Categories
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-2">
              <PieChart className="h-4 w-4" />
              Reports
            </TabsTrigger>
          </TabsList>

          {/* Ledger Tab */}
          <TabsContent value="ledger" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle>Transaction Ledger</CardTitle>
                    <CardDescription>
                      Showing {filteredTransactions.length} transactions
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <Input
                        type="date"
                        value={dateFilter.start}
                        onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
                        className="w-36"
                      />
                      <span className="text-muted-foreground">to</span>
                      <Input
                        type="date"
                        value={dateFilter.end}
                        onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
                        className="w-36"
                      />
                    </div>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="income">Income</SelectItem>
                        <SelectItem value="expense">Expense</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filtered Summary */}
                <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Period Income</p>
                    <p className="text-lg font-semibold text-green-600">₹{filteredStats.income.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Period Expense</p>
                    <p className="text-lg font-semibold text-red-600">₹{filteredStats.expense.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Period Balance</p>
                    <p className={`text-lg font-semibold ${filteredStats.income - filteredStats.expense >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                      ₹{(filteredStats.income - filteredStats.expense).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>

                <TransactionTable transactions={filteredTransactions} categories={categories} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-green-600 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Income Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {categories.filter(c => c.type === 'income').length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">No income categories</p>
                  ) : (
                    <div className="space-y-2">
                      {categories.filter(c => c.type === 'income').map(cat => (
                        <div key={cat.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{cat.name}</p>
                            {cat.description && <p className="text-xs text-muted-foreground">{cat.description}</p>}
                          </div>
                          <Badge variant="success" className="bg-green-100 text-green-800">Income</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-red-600 flex items-center gap-2">
                    <TrendingDown className="h-5 w-5" />
                    Expense Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {categories.filter(c => c.type === 'expense').length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">No expense categories</p>
                  ) : (
                    <div className="space-y-2">
                      {categories.filter(c => c.type === 'expense').map(cat => (
                        <div key={cat.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{cat.name}</p>
                            {cat.description && <p className="text-xs text-muted-foreground">{cat.description}</p>}
                          </div>
                          <Badge variant="destructive" className="bg-red-100 text-red-800">Expense</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-green-600">Income by Category</CardTitle>
                  <CardDescription>Top income sources (filtered period)</CardDescription>
                </CardHeader>
                <CardContent>
                  {getCategorySummary('income').length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">No income data</p>
                  ) : (
                    <div className="space-y-3">
                      {getCategorySummary('income').map(cat => (
                        <div key={cat.id} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span>{cat.name}</span>
                            <span className="font-semibold text-green-600">₹{cat.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500 rounded-full"
                              style={{ width: `${filteredStats.income > 0 ? (cat.total / filteredStats.income) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-red-600">Expense by Category</CardTitle>
                  <CardDescription>Top expense areas (filtered period)</CardDescription>
                </CardHeader>
                <CardContent>
                  {getCategorySummary('expense').length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">No expense data</p>
                  ) : (
                    <div className="space-y-3">
                      {getCategorySummary('expense').map(cat => (
                        <div key={cat.id} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span>{cat.name}</span>
                            <span className="font-semibold text-red-600">₹{cat.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-red-500 rounded-full"
                              style={{ width: `${filteredStats.expense > 0 ? (cat.total / filteredStats.expense) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

const TransactionTable = ({ transactions, categories }) => {
  if (transactions.length === 0) {
    return <p className="text-center text-muted-foreground py-8">No transactions found for this period</p>;
  }

  // Sort by date descending
  const sortedTransactions = [...transactions].sort((a, b) => new Date(b.date) - new Date(a.date));

  const getSourceBadge = (source) => {
    const sourceColors = {
      fee_collection: 'bg-green-100 text-green-800',
      salary: 'bg-red-100 text-red-800',
      store: 'bg-blue-100 text-blue-800',
      manual: 'bg-purple-100 text-purple-800'
    };
    const sourceLabels = {
      fee_collection: 'Fee',
      salary: 'Salary',
      store: 'Store',
      manual: 'Manual'
    };
    return (
      <Badge className={sourceColors[source] || 'bg-gray-100 text-gray-800'}>
        {sourceLabels[source] || source}
      </Badge>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="text-left py-3 px-4 font-medium text-sm">Date</th>
            <th className="text-left py-3 px-4 font-medium text-sm">Source</th>
            <th className="text-left py-3 px-4 font-medium text-sm">Type</th>
            <th className="text-left py-3 px-4 font-medium text-sm">Category</th>
            <th className="text-left py-3 px-4 font-medium text-sm">Description</th>
            <th className="text-left py-3 px-4 font-medium text-sm">Ref#</th>
            <th className="text-right py-3 px-4 font-medium text-sm">Amount</th>
          </tr>
        </thead>
        <tbody>
          {sortedTransactions.map((transaction, index) => {
            const category = categories.find(c => c.id === transaction.category_id);
            return (
              <tr 
                key={transaction.id} 
                className="border-b hover:bg-muted/50 transition-colors"
                data-testid={`transaction-row-${index}`}
              >
                <td className="py-3 px-4 text-sm">{transaction.date}</td>
                <td className="py-3 px-4">{getSourceBadge(transaction.source)}</td>
                <td className="py-3 px-4">
                  <Badge variant={transaction.type === 'income' ? 'success' : 'destructive'} className={transaction.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {transaction.type === 'income' ? 'Income' : 'Expense'}
                  </Badge>
                </td>
                <td className="py-3 px-4 text-sm">{transaction.category || category?.name || '-'}</td>
                <td className="py-3 px-4 text-sm max-w-[200px] truncate">{transaction.description}</td>
                <td className="py-3 px-4 text-sm text-muted-foreground">{transaction.reference || '-'}</td>
                <td className="py-3 px-4 text-right">
                  <span className={`font-semibold ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                    {transaction.type === 'income' ? '+' : '-'}₹{transaction.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AccountsPage;
